import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateMatchProfile } from "~pages/MatchProfiles/types/upsertMatchProfile.ts";
import { useCreateMatchProfile } from "./useCreateMatchProfile.ts";
import { useDeleteMatchProfile } from "./useDeleteMatchProfile.ts";
import { useUpdateMatchProfile } from "./useUpdateMatchProfile.ts";

export const useMatchProfileService = () => {
  const { mutateAsync: createMatchProfileFn, isPending: isCreatePending } = useCreateMatchProfile();
  const { mutateAsync: updateMatchProfileFn, isPending: isUpdatePending } = useUpdateMatchProfile();
  const { mutateAsync: deleteMatchProfileFn, isPending: isDeletePending } = useDeleteMatchProfile();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove } = useApiOperations(ApiOperationItems.MATCH_PROFILE);

  const createMatchProfile = async (payload: CreateMatchProfile, searchName?: string) => {
    const result = await create(() => createMatchProfileFn(payload), {
      successMessageId: "match.profile.add.success",
      isSucceeded: () => true,
    });

    if (result !== undefined) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/match-profiles`, {
        state: { search: typeof searchName === "string" ? searchName : undefined },
      });
    }

    return result;
  };

  const updateMatchProfile = (profileId: string, payload: Record<string, unknown>) =>
    update(() => updateMatchProfileFn({ ...payload, profileId }), {
      successMessageId: "match.profile.update.success",
    });

  const deleteMatchProfile = async (profileId: string) =>
    Boolean(
      await remove(() => deleteMatchProfileFn(profileId), {
        successMessageId: "match.profile.delete.success",
      }),
    );

  return {
    createMatchProfile,
    isCreatePending,
    updateMatchProfile,
    isUpdatePending,
    deleteMatchProfile,
    isDeletePending,
  };
};
